import { useState } from 'react'
import { generatePath, Link } from 'react-router-dom'

import { FormattedCalendarDate } from '@/frontend/components/formatted-datetime'
import { Pagination } from '@/frontend/components/pagination'
import { SearchInput } from '@/frontend/components/search-input'
import { Badge } from '@/frontend/components/ui/badge'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { Label } from '@/frontend/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useListPatients } from '@/frontend/hooks/api/patients/use-list-patients'
import { useDebounce } from '@/frontend/hooks/use-debounce'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'
import { cn } from '@/frontend/lib/utils'
import { routes } from '@/frontend/routes'

const PAGE_SIZE = 20

export function PatientsPage() {
  useDocumentTitle('Patients')
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounce(search, 300)
  const q = debouncedSearch.length >= 2 ? debouncedSearch : undefined
  const offset = (page - 1) * PAGE_SIZE
  const { region } = useViewerRegion()
  // DOB is a calendar day, not an instant — no timezone applies, only the viewer's locale.
  const { data, isLoading, error } = useListPatients({
    q,
    limit: PAGE_SIZE,
    offset,
    includeArchived: showArchived,
    region,
  })

  const patients = data?.patients
  const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0

  /** Reset to page 1 when the search query changes. */
  function handleSearchChange(value: string) {
    setSearch(value)
    setPage(1)
  }

  /** Reset to page 1 when the archived filter is toggled. */
  function handleShowArchivedChange(checked: boolean) {
    setShowArchived(checked)
    setPage(1)
  }

  if (error) return <div>Error: {error.message}</div>

  return (
    <div>
      <h1 className="text-headline-lg mb-4">Patients</h1>
      <div className="mb-4 flex items-center gap-4">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          placeholder="Search by patient or owner name, email, phone, microchip..."
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-archived"
            checked={showArchived}
            onCheckedChange={(checked) => handleShowArchivedChange(checked === true)}
          />
          <Label htmlFor="show-archived" className="font-normal cursor-pointer">
            Show archived
          </Label>
        </div>
      </div>
      {isLoading ? (
        <div>Loading...</div>
      ) : patients?.length === 0 ? (
        <p className="text-muted-foreground">{q ? 'No patients found' : 'No patients yet'}</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Member</TableHead>
                <TableHead>Species</TableHead>
                <TableHead>Breed</TableHead>
                <TableHead>Sex</TableHead>
                <TableHead>Date of Birth</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {patients?.map((patient) => (
                <TableRow key={patient.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Link
                        to={generatePath(routes.patient, { patientId: patient.id })}
                        className={cn(
                          'hover:underline',
                          patient.archivedAt && 'text-muted-foreground'
                        )}
                      >
                        {patient.name}
                      </Link>
                      {patient.archivedAt && (
                        <Badge variant="secondary" size="sm">
                          Archived
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {patient.owner.firstName} {patient.owner.lastName}
                  </TableCell>
                  <TableCell>{patient.species}</TableCell>
                  <TableCell>{patient.breed ?? '—'}</TableCell>
                  <TableCell>{patient.sex}</TableCell>
                  <TableCell>
                    {patient.dateOfBirth ? (
                      <FormattedCalendarDate value={patient.dateOfBirth} />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}
